import { useEffect, useRef } from "react";

type FieldPoint = {
  x: number;
  y: number;
  length: number;
  radius: number;
};

type Vector = { x: number; y: number };

const POINT_COUNT = 480;
const BODY_NODE_COUNT = 9;
const LEG_STRAND_COUNT = 2;
const SPIDER_COUNT = 2;
const ACTIVE_POINT_LIMIT = 8;
const BOLT_SEGMENTS = 13;
const LEG_TRANSITION_STEP = 0.85;
const MAX_PIXEL_RATIO = 1.5;
const MIN_FRAME_TIME = 1000 / 70;

export function SpiderField() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvasElement = canvasRef.current;
    if (!canvasElement) return;
    const drawingContext = canvasElement.getContext("2d");
    const container = canvasElement.parentElement;
    if (!drawingContext || !container) return;
    const surface: HTMLCanvasElement = canvasElement;
    const ctx: CanvasRenderingContext2D = drawingContext;
    const host: HTMLElement = container;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    const spaciousViewport = window.matchMedia("(min-width: 821px)");
    const { cos, hypot, max, min, PI, sin } = Math;

    let width = 0;
    let height = 0;
    let frame = 0;
    let visible = false;
    let documentVisible = document.visibilityState === "visible";
    let canAnimate = finePointer.matches && spaciousViewport.matches && !reducedMotion.matches;
    let lastPaintTime = 0;
    let spiders: ReturnType<typeof spawn>[] = [];

    function many<T>(count: number, create: (index: number) => T) {
      return Array.from({ length: count }, (_, index) => create(index));
    }

    function random(range = 1, offset = 0) {
      return Math.random() * range + offset;
    }

    function lerp(start: number, end: number, amount: number) {
      return start + (end - start) * amount;
    }

    function sparkNoise(value: number) {
      const raw = sin(value) * 43758.5453;
      return (raw - Math.floor(raw)) * 2 - 1;
    }

    function drawCircle(x: number, y: number, radius: number) {
      ctx.beginPath();
      ctx.ellipse(x, y, radius, radius, 0, 0, PI * 2);
      ctx.fill();
    }

    function drawBolt(
      startX: number,
      startY: number,
      endX: number,
      endY: number,
      time: number,
      boltSeed: number,
    ) {
      const deltaX = endX - startX;
      const deltaY = endY - startY;
      const distance = hypot(deltaX, deltaY);
      if (distance < 0.5) return;
      const tangentX = deltaX / distance;
      const tangentY = deltaY / distance;
      const normalX = -tangentY;
      const normalY = tangentX;
      const displacement = min(14, distance * 0.13);
      const pulse = Math.floor(time * 16);
      const flicker = (sin(time * 31 + boltSeed * 4.7) + 1) / 2;

      ctx.beginPath();
      ctx.moveTo(startX, startY);
      for (let index = 1; index <= BOLT_SEGMENTS; index += 1) {
        const amount = index / BOLT_SEGMENTS;
        if (index === BOLT_SEGMENTS) {
          ctx.lineTo(endX, endY);
          continue;
        }
        const envelope = sin(amount * PI);
        const lateralNoise = sparkNoise(index * 12.9898 + boltSeed * 78.233 + pulse * 0.713);
        const secondaryNoise = sparkNoise(index * 39.346 + boltSeed * 11.135 + pulse * 1.173);
        const longitudinalNoise = sparkNoise(index * 21.173 - boltSeed * 9.217 + pulse * 0.431);
        const lateralOffset = (lateralNoise * 0.72 + secondaryNoise * 0.28) * displacement * envelope;
        const longitudinalOffset = longitudinalNoise * 1.25 * envelope;
        const x = lerp(startX, endX, amount) + normalX * lateralOffset + tangentX * longitudinalOffset;
        const y = lerp(startY, endY, amount) + normalY * lateralOffset + tangentY * longitudinalOffset;
        ctx.lineTo(x, y);
      }
      ctx.globalAlpha = 0.64 + flicker * 0.36;
      ctx.lineWidth = 0.72 + flicker * 0.42;
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    function spawn() {
      const points = many<FieldPoint>(POINT_COUNT, () => ({
        x: random(width),
        y: random(height),
        length: 0,
        radius: 0,
      }));
      const directions = many<Vector>(BODY_NODE_COUNT, (index) => ({
        x: cos((index / BODY_NODE_COUNT) * PI * 2),
        y: sin((index / BODY_NODE_COUNT) * PI * 2),
      }));
      const seed = random(100);
      const frequencyX = random(0.5, 0.5);
      const frequencyY = random(0.5, 0.5);
      const walkRadius = { x: random(50, 50), y: random(50, 50) };
      const bodyRadius = width / random(100, 150);
      let targetX = random(width);
      let targetY = random(height);
      let x = random(width);
      let y = random(height);

      const paintLegs = (point: FieldPoint, time: number, pointIndex: number) => {
        if (point.length > 0) {
          for (let strandIndex = 0; strandIndex < LEG_STRAND_COUNT; strandIndex += 1) {
            const directionIndex = (pointIndex + strandIndex * 4) % BODY_NODE_COUNT;
            const direction = directions[directionIndex];
            if (!direction) continue;
            drawBolt(
              lerp(x + direction.x * bodyRadius, point.x, point.length * point.length),
              lerp(y + direction.y * bodyRadius, point.y, point.length * point.length),
              x + direction.x * bodyRadius,
              y + direction.y * bodyRadius,
              time,
              seed + pointIndex * 0.73 + directionIndex * 1.91,
            );
          }
        }
      };

      return {
        follow(nextX: number, nextY: number) {
          targetX = nextX;
          targetY = nextY;
        },
        paint(time: number) {
          const selfMoveX = cos(time * frequencyX + seed) * walkRadius.x;
          const selfMoveY = sin(time * frequencyY + seed) * walkRadius.y;
          const focusX = targetX + selfMoveX;
          const focusY = targetY + selfMoveY;

          x += min(width / 100, (focusX - x) / 10);
          y += min(width / 100, (focusY - y) / 10);

          let activePoints = 0;
          points.forEach((point, pointIndex) => {
            const distance = hypot(point.x - x, point.y - y);
            let radius = min(2, width / distance / 5);
            const connected = distance < width / 10 && activePoints++ < ACTIVE_POINT_LIMIT;
            point.length = max(0, min(point.length + (connected ? LEG_TRANSITION_STEP : -LEG_TRANSITION_STEP), 1));
            if (connected) radius *= 1.5;
            point.radius = radius;
            paintLegs(point, time, pointIndex);
          });

          ctx.globalAlpha = 1;
          ctx.beginPath();
          points.forEach((point) => {
            ctx.moveTo(point.x + point.radius, point.y);
            ctx.arc(point.x, point.y, point.radius, 0, PI * 2);
          });
          ctx.fill();
        },
      };
    }

    function paintBackground() {
      ctx.fillStyle = "#090a0d";
      ctx.fillRect(0, 0, width, height);
    }

    function paintStaticField() {
      paintBackground();
      ctx.fillStyle = "rgba(242, 244, 247, 0.48)";
      const count = Math.max(110, Math.min(POINT_COUNT * SPIDER_COUNT, Math.round((width * height) / 3200)));
      many(count, (index) => {
        const x = ((index * 83.7) % 100) / 100 * width;
        const y = ((index * 47.3 + 19) % 100) / 100 * height;
        drawCircle(x, y, index % 19 === 0 ? 1.25 : 0.65);
      });
    }

    function resize() {
      const rect = host.getBoundingClientRect();
      const pixelRatio = min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
      width = max(1, rect.width);
      height = max(1, rect.height);
      surface.width = Math.round(width * pixelRatio);
      surface.height = Math.round(height * pixelRatio);
      surface.style.width = `${width}px`;
      surface.style.height = `${height}px`;
      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      ctx.lineWidth = 1;
      spiders = many(SPIDER_COUNT, spawn);
      paintStaticField();
    }

    function stop() {
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
      lastPaintTime = 0;
    }

    function render(time: number) {
      frame = 0;
      if (!visible || !documentVisible || !canAnimate) return;
      if (lastPaintTime && time - lastPaintTime < MIN_FRAME_TIME) {
        frame = window.requestAnimationFrame(render);
        return;
      }
      lastPaintTime = time;
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = "#fff";
      ctx.strokeStyle = "rgba(255, 255, 255, 0.96)";
      ctx.lineWidth = 0.82;
      spiders.forEach((spider) => spider.paint(time / 1000));
      frame = window.requestAnimationFrame(render);
    }

    function start() {
      if (!frame && visible && documentVisible && canAnimate) {
        frame = window.requestAnimationFrame(render);
      }
    }

    const handlePointerMove = (event: PointerEvent) => {
      const rect = host.getBoundingClientRect();
      const pointerX = event.clientX - rect.left;
      const pointerY = event.clientY - rect.top;
      spiders.forEach((spider) => spider.follow(pointerX, pointerY));
    };

    const handleCapabilityChange = () => {
      canAnimate = finePointer.matches && spaciousViewport.matches && !reducedMotion.matches;
      if (canAnimate) start();
      else {
        stop();
        paintStaticField();
      }
    };

    const handleVisibilityChange = () => {
      documentVisible = document.visibilityState === "visible";
      if (documentVisible) start();
      else stop();
    };

    resize();
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(host);
    const visibilityObserver = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting);
      if (visible) start();
      else stop();
    }, { threshold: 0.01 });
    visibilityObserver.observe(host);
    host.addEventListener("pointermove", handlePointerMove, { passive: true });
    reducedMotion.addEventListener("change", handleCapabilityChange);
    finePointer.addEventListener("change", handleCapabilityChange);
    spaciousViewport.addEventListener("change", handleCapabilityChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stop();
      resizeObserver.disconnect();
      visibilityObserver.disconnect();
      host.removeEventListener("pointermove", handlePointerMove);
      reducedMotion.removeEventListener("change", handleCapabilityChange);
      finePointer.removeEventListener("change", handleCapabilityChange);
      spaciousViewport.removeEventListener("change", handleCapabilityChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return <canvas aria-hidden="true" className="spider-field__canvas" ref={canvasRef} />;
}
